import { useState, useEffect, useCallback } from 'react';
import { getTenantId, getTenantConfig } from '../services/tenant/tenantService.js';

export const useTenant = () => {
  const [tenantId, setTenantId] = useState(getTenantId());
  const [config, setConfig] = useState(getTenantConfig());

  useEffect(() => {
    const handleStorageChange = () => {
      setTenantId(getTenantId());
      setConfig(getTenantConfig());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const isFeatureEnabled = useCallback((feature) => {
    return config?.FEATURE_FLAGS?.[feature] ?? false;
  }, [config]);

  const getSignupType = useCallback(() => {
    return config?.SIGNUP_TYPE ?? 'OPENED';
  }, [config]);

  const hasCustomUsername = useCallback(() => {
    return config?.CONFIGS?.HAS_CUSTOM_USERNAME;
  }, [config]);

  const getLoginCaseType = useCallback(() => {
    return config?.CONFIGS?.LOGIN_CASE ?? 'NONE';
  }, [config]);

  const getLoginFieldType = useCallback(() => {
    const fieldType = config?.CONFIGS?.LOGIN_FIELD_TYPE ?? 'DOCUMENT';
    if (['DOCUMENT', 'EMAIL', 'PHONE', 'TEXT', 'CUSTOM'].includes(fieldType)) {
      return fieldType;
    }
    return 'DOCUMENT';
  }, [config]);

  const getLoginFieldMask = useCallback(() => {
    return config?.CONFIGS?.LOGIN_FIELD_MASK ?? null;
  }, [config]);

  const getExtraFields = useCallback((documentType = null) => {
    const extraFields = config?.EXTRA_FIELDS ?? [];
    if (!documentType) {
      return extraFields;
    }
    return extraFields.filter(field => {
      if (!field.conditions?.documentType) {
        return true;
      }
      return field.conditions.documentType.includes(documentType);
    }).sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [config]);

  const isBirthDateHidden = useCallback(() => {
    return config?.CONFIGS?.HIDE_BIRTH_DATE ?? false;
  }, [config]);

  const isNameDisabled = useCallback(() => {
    return config?.CONFIGS?.DISABLE_NAME ?? false;
  }, [config]);

  // Para campanhas/tenants cujo "documento" não é CPF/CNPJ (ex.: matrícula,
  // código interno). Desliga a máscara/validação de CPF-CNPJ do campo
  // documento e para de inferir PJ/PF pelo tamanho (11/14 dígitos) — sem
  // isso, um documento pré-carregado que não bate com o formato trava o
  // usuário: o campo some/desabilita, o valor nunca valida, e o botão de
  // avançar nunca habilita.
  const isDocumentMaskDisabled = useCallback(() => {
    return config?.CONFIGS?.DISABLE_DOCUMENT_MASK ?? false;
  }, [config]);

  const getAuthenticationType = useCallback(() => {
    return config?.AUTHENTICATION_TYPE || null;
  }, [config]);

  const getProfileSelect = useCallback(() => {
    return config?.PROFILE_SELECT ?? null;
  }, [config]);

  return {
    tenantId,
    config,
    isFeatureEnabled,
    getSignupType,
    hasCustomUsername,
    getLoginCaseType,
    getLoginFieldType,
    getLoginFieldMask,
    getExtraFields,
    isBirthDateHidden,
    isNameDisabled,
    isDocumentMaskDisabled,
    getAuthenticationType,
    getProfileSelect,
  };
};
